import { inject, Injectable } from '@angular/core';
import { ApiService } from '@brighthub/api';
import { environment } from '@brighthub/environment';
import { AuthService } from '@brightside/core/auth';
import type { ChatSuggestion } from '../models/chat-suggestions-api.model';
import {
  extractPublicSuggestionList,
  type ChatSuggestionsPublicListHttpInput,
} from './chat-suggestions-envelope.util';
import { shuffleChatSuggestions } from '../utils/shuffle-chat-suggestions';
import { from, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';

export type ChatSuggestionItem = ChatSuggestion;

/** Drives session memoization: 200 responses (including intentional empty lists) vs transient failures. */
interface SessionSuggestionsLoadResult {
  items: ChatSuggestion[];
  memoizeSession: boolean;
}

/**
 * Loads copilot chat suggestion chips for the signed-in user via `brightnav-api`, scoped by
 * `agentId`, and memoizes a successful response per agent for the SPA session. Used by FA chat
 * chips and Ask Navi welcome prompts; {@link ChatSuggestionsAdminService} clears this cache after
 * admin mutations.
 */
@Injectable({
  providedIn: 'root',
})
export class ChatSuggestionsService {
  private readonly _api = inject(ApiService);
  private readonly _auth = inject(AuthService);

  /** One observable per `agentId` after a memoizable fetch for the SPA session. */
  private readonly _sessionSuggestionsByAgent = new Map<string, Observable<ChatSuggestion[]>>();

  /**
   * Active suggestions for one agent — cached after the first fetch that completes with HTTP 200
   * (including an intentionally empty list). Transient empty results are not memoized.
   *
   * @param agentId Chat agent metadata `agent_id` (e.g. `brightnav`, `content`). Trimmed before cache lookup and API call; whitespace-only values return an empty list without a request.
   */
  getSuggestions(agentId: string): Observable<ChatSuggestion[]> {
    const normalizedAgentId = agentId.trim();
    if (!normalizedAgentId) {
      return of([]);
    }

    const cached = this._sessionSuggestionsByAgent.get(normalizedAgentId);
    if (cached) {
      return cached;
    }

    const stream$ = this._fetchSuggestionsFromApi(normalizedAgentId).pipe(
      tap((result) => {
        if (!result.memoizeSession) {
          this._sessionSuggestionsByAgent.delete(normalizedAgentId);
        }
      }),
      map((result) => result.items),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this._sessionSuggestionsByAgent.set(normalizedAgentId, stream$);
    return stream$;
  }

  /** Clears all per-agent session caches (e.g. after logout or admin writes). */
  clearSessionCache(): void {
    this._sessionSuggestionsByAgent.clear();
  }

  /**
   * GET /v1/prototype/chat/suggestions?agentId=… (brightnav-api).
   *
   * @remarks Uses {@link ApiService} so auth matches other brightnav calls. Skips the request when
   * there is no access token (returns a non-memoized empty list so a later call can retry).
   */
  private _fetchSuggestionsFromApi(agentId: string): Observable<SessionSuggestionsLoadResult> {
    if (!environment.apiEndpoints['brightnav-api']) {
      return of({ items: [], memoizeSession: false });
    }

    const path = `/v1/prototype/chat/suggestions?agentId=${encodeURIComponent(agentId)}`;

    return from(this._auth.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          return of({ items: [], memoizeSession: false });
        }

        return this._api.get<ChatSuggestionsPublicListHttpInput>('brightnav-api', path).pipe(
          map((res): SessionSuggestionsLoadResult => {
            const parsed = extractPublicSuggestionList(res, agentId);
            return {
              items: shuffleChatSuggestions(parsed.items),
              memoizeSession: parsed.memoizeSession,
            };
          }),
          catchError(() => of({ items: [], memoizeSession: false }))
        );
      }),
      catchError(() => of({ items: [], memoizeSession: false }))
    );
  }
}
